/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import "./RateOrder.css";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { RateProduct } from "./RateProduct";
import { RateSeller } from "./RateSeller";
import { StarsRating } from "./StarsRating";
import useQuery from "./useQuery";
import { Ring } from "./Ring";
import { NpsScore } from "./NpsScore";
import { NetPromoterScoreSurvey } from "./NetPromoterScoreSurvey";
import { useTranslation } from "react-i18next";
import closeButton from "./close_button_inverse.png";

export function RateOrder(props) {
  const { t, i18n } = useTranslation();
  const { external } = props;
  const { customer, orderToken, orderId, sku: skuParam } = useParams();
  const query = useQuery();
  // Prioriza URL param, fallback para query string
  const sku = skuParam || query.get("sku");
  const [orderItems, setOrderItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRating, setSelectedRating] = useState(null);
  const [npsRating, setNpsRating] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isNpsModalVisible, setIsNpsModalVisible] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coupon, setCoupon] = useState(false);
  const [npsSurvey, setNpsSurvey] = useState(null);
  const [copyCoupon, setCopyCoupon] = useState(false);
  const [allowCoupon, setAllowCoupon] = useState(false);
  const [storeReview, setStoreReview] = useState([]);
  const [sellerReview, setSellerReview] = useState(null);

  useEffect(() => {
    setTimeout(() => {
      setCopyCoupon(false);
    }, 5000);
  }, [copyCoupon]);

  const handleCopy = (e, code) => {
    e.preventDefault();
    setCopyCoupon(true);
    navigator.clipboard.writeText(code);
  };

  const getCouponMetadata = async () => {
    try {
      const source = query.get("source");
      const url = `${process.env.REACT_APP_API_URL}/${customer}/order/${orderId}/coupon-metadata${source ? `?source=${source}` : ''}`;
      
      var couponData = await axios.get(url);

      // Se retornar 204 (No Content), não há cupom elegível
      if (couponData.status === 204 || !couponData.data) {
        setCoupon(null);
        return;
      }

      // Armazena apenas os metadados (sem o código) para incentivo
      setCoupon(couponData.data);
    } catch (err) {
      console.error('Erro ao buscar metadados do cupom:', err);
      // Limpa cupom em caso de erro para evitar inconsistências
      setCoupon(null);
    }
  };

  const getOrderDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      var customerData = await axios.get(
        `${process.env.REACT_APP_API_URL}/${customer}/info`
      );

      // Verificar se os dados do cliente foram encontrados
      if (!customerData.data || customerData.data === null) {
        const error = new Error("Cliente não encontrado");
        error.response = { status: 404 };
        throw error;
      }

      setCustomerInfo(customerData.data);

      let orderDataUrl = "";
      if (orderToken && orderToken !== "") {
        if (!external)
          orderDataUrl = `${process.env.REACT_APP_API_URL}/${customer}/orders/${orderToken}/${orderId}`;
        else
          orderDataUrl = `${process.env.REACT_APP_API_URL}/${customer}/external-orders/${orderToken}/${orderId}`;
      } else if (sku && sku !== "") {
        orderDataUrl = `${process.env.REACT_APP_API_URL}/${customer}/orders/${orderId}/sku/${sku}`;
      }

      var orderData = await axios.get(orderDataUrl);
      
      // Verificar se o pedido foi encontrado
      if (!orderData.data || orderData.data === null) {
        const error = new Error("Pedido não encontrado");
        error.response = { status: 404 };
        throw error;
      }
      
      setOrderItems(orderData.data);

      if (customerData.data.settings.embedNpsSurvey) {
        try {
          const npsData = await axios.get(
            `${
              process.env.REACT_APP_API_URL
            }/${customer}/nps/order/${orderId}?token=${orderToken ?? ""}&sku=${
              sku ?? ""
            }`
          );
          setNpsSurvey(npsData.data);
        } catch (npsErr) {
          console.error('Erro ao buscar pesquisa NPS:', npsErr);
          // Não definir erro global para NPS, apenas logar
        }
      }

      if (customerData.data.settings?.enableStoreReview && !external) {
        if (!orderData.data.products[0]) {
          return;
        }

        setStoreReview({
          active: orderData.data.storeReview === null ? true : false,
          isStore: true,
          customer: customer,
          logo: customerData.data.logo,
          orderId: orderId,
          sku: "store-reviews",
          name: customerData.data.name,
          _id: orderData.data.products[0]._id,
          userId: orderData.data.products[0].userId,
          rating: orderData.data.storeReview?.rating || 0,
        });
      }

      // if API returns seller groups, we can render them too
      if (orderData.data?.sellers && orderData.data.sellers.length > 0) {
        // nothing else to do here; render in list below
      }

      await getCouponMetadata();

      if (!firstLoaded) setFirstLoaded(true);
    } catch (err) {
      console.error('Erro ao buscar detalhes do pedido:', err);
      let errorMessage = t('error.order_fetch');
      
      if (err.response?.status === 404) {
        // Verificar se é erro de cliente ou pedido baseado na mensagem
        if (err.message?.includes('Cliente não encontrado')) {
          errorMessage = t('error.customer_not_found');
        } else {
          errorMessage = t('error.order_not_found');
        }
      } else if (err.response?.status === 403) {
        errorMessage = t('error.order_access_denied');
      } else if (err.response?.status >= 500) {
        errorMessage = t('error.server_error');
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para obter todos os itens ativos
  const getActiveItems = () => {
    const activeItems = [];
    
    // Respeita a ordem baseada em storeReviewFirst
    if (customerInfo?.settings?.storeReviewFirst) {
      // Store review primeiro
      if (customerInfo?.settings?.enableStoreReview && !external && storeReview.active) {
        activeItems.push({ ...storeReview, type: 'store' });
      }
    }
    
    // Adiciona produtos ativos
    if (orderItems.products) {
      orderItems.products.forEach((item) => {
        if (item.active) {
          activeItems.push({ ...item, type: 'product' });
        }
      });
    }
    
    // Adiciona sellers ativos
    if (customerInfo?.settings?.enableSellerReview && orderItems.sellers) {
      orderItems.sellers.forEach((seller) => {
        if (seller.active) {
          activeItems.push({ ...seller, type: 'seller' });
        }
      });
    }
    
    // Store review no final (se não for first)
    if (!customerInfo?.settings?.storeReviewFirst) {
      if (customerInfo?.settings?.enableStoreReview && !external && storeReview.active) {
        activeItems.push({ ...storeReview, type: 'store' });
      }
    }
    
    return activeItems;
  };

  // Função para obter o próximo item ativo
  const getNextActiveItem = (currentProduct) => {
    if (!currentProduct) return null;
    
    const activeItems = getActiveItems();
    
    // Encontra o índice do item atual
    let currentIndex = -1;
    
    for (let i = 0; i < activeItems.length; i++) {
      const item = activeItems[i];
      
      if (item.type === 'product' && currentProduct.sku === item.sku) {
        currentIndex = i;
        break;
      } else if (item.type === 'seller' && currentProduct.sellerId === item.sellerId) {
        currentIndex = i;
        break;
      } else if (item.type === 'store' && currentProduct.isStore) {
        currentIndex = i;
        break;
      }
    }
    
    // Se encontrou e há próximo item
    if (currentIndex >= 0 && currentIndex < activeItems.length - 1) {
      return activeItems[currentIndex + 1];
    }
    
    return null;
  };

  // Handler para navegar para o próximo item
  const handleNavigateToNext = async () => {
    const nextItem = getNextActiveItem(selectedProduct);
    
    if (nextItem) {
      if (nextItem.type === 'product' || nextItem.type === 'store') {
        setSelectedProduct(nextItem);
        setIsModalVisible(true);
        window.scrollTo(0, 0);
      } else if (nextItem.type === 'seller') {
        setIsModalVisible(false);
        setSellerReview(nextItem);
      }
      
      // Recarrega pedido em background para atualizar status dos cards
      await getOrderDetails();
    } else {
      // Não há próximo item, recarrega e fecha o modal
      await getOrderDetails();
      setIsModalVisible(false);
      setSelectedProduct(null);
    }
  };

  useEffect(() => {
    getOrderDetails();
  }, [orderToken, sku]);

  useEffect(() => {
    if (firstLoaded) {
      var ratingClicked = query.get("ratingClicked");
      var npsRating = query.get("npsRating");
      
      // Verifica se há rating clicked na query string
      if (ratingClicked) {
        var item = orderItems.products.find((x) => x.sku === sku);
        if (
          item &&
          item.active &&
          ratingClicked &&
          ratingClicked >= 1 &&
          ratingClicked <= 5
        ) {
          setSelectedProduct(item);
          setSelectedRating(ratingClicked);
          setTimeout(() => {
            setIsModalVisible(true);
          }, 500);
        }
      } else {
        // Auto-abertura se houver apenas 1 item ativo
        const activeItems = getActiveItems();
        if (activeItems.length === 1) {
          const singleItem = activeItems[0];
          
          if (singleItem.type === 'product') {
            setSelectedProduct(singleItem);
            setTimeout(() => {
              setIsModalVisible(true);
              window.scrollTo(0, 0);
            }, 500);
          } else if (singleItem.type === 'seller') {
            setSellerReview(singleItem);
          } else if (singleItem.type === 'store') {
            setSelectedProduct(singleItem);
            setTimeout(() => {
              setIsModalVisible(true);
              window.scrollTo(0, 0);
            }, 500);
          }
        }
      }
    }

    if (npsRating) {
      setNpsRating(parseInt(npsRating));
      setTimeout(() => {
        setIsNpsModalVisible(true);
      }, 500);
    }
  }, [firstLoaded]);

  useEffect(() => {
    if (customerInfo?.settings?.locale) {
      i18n.changeLanguage(customerInfo.settings.locale);
    }
  }, [customerInfo]);

  return (
    <div className="rate-order-page">
      {customerInfo && (
        <>
          {customerInfo.importCssWebApp && (
            <link rel="stylesheet" href={customerInfo.importCssWebApp} />
          )}

          <div className="logo">
            {customerInfo && (
              <img
                src={customerInfo.logo}
                alt={t("alt.customer_logo", { name: customerInfo.name })}
              />
            )}
          </div>
          {isLoading && (
            <div className="loading-container">
              <Ring />
              <p>{t('loading.order_details')}</p>
            </div>
          )}
          {error && (
            <div className="error-container">
              <div className="error-icon">
                <i className="bi bi-exclamation-triangle"></i>
              </div>
              <h2>{t('error.title')}</h2>
              <p>{error}</p>
              <button 
                onClick={() => {
                  setError(null);
                  getOrderDetails();
                }} 
                className="btn-retry"
              >
                {t('error.retry')}
              </button>
            </div>
          )}
          {!isLoading && !error && (
            <>
              <h3>
                {orderItems.products?.some((x) => x.active)
                  ? t("rate_order_title")
                  : t("all_products_rated")}
              </h3>
              <div
                className={`order-items ${
                  customerInfo.settings.storeReviewFirst
                    ? "store-review-first"
                    : ""
                }`}
              >
                {orderItems.products.map((item) => {
                  return (
                    <div
                      key={item.sku}
                      className={`order-item ${item.active ? "" : "done"}`}
                      onClick={() => {
                        if (item.active) {
                          setSelectedProduct(item);
                          setIsModalVisible(true);
                          window.scrollTo(0, 0);
                        }
                      }}
                    >
                      {!item.active && <div className="badge"></div>}
                      <img
                        className="picture"
                        alt={t("alt.product_image", {
                          name: item.product?.name ?? item.sku,
                        })}
                        src={
                          item.product?.image
                            ? item.product.image
                            : "/image.jpeg"
                        }
                      />
                      <div className="details">
                        <div className="item-name">
                          {item.product?.name ?? t("ref", { sku: item.sku })}
                        </div>
                        <div className="rating">
                          <StarsRating
                            value={item.review ? item.review.rating : 0}
                            showText={false}
                            onClickDisabled={(value) => {
                              setSelectedRating(value);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {customerInfo.settings.enableStoreReview && !external && (
                  <div
                    className={`order-item store-review ${
                      storeReview.active ? "" : "done"
                    }`}
                    onClick={() => {
                      if (storeReview.active) {
                        setSelectedProduct(storeReview);
                        setIsModalVisible(true);
                        window.scrollTo(0, 0);
                      }
                    }}
                  >
                    {!storeReview.active && <div className="badge"></div>}
                    <img
                      alt={t("alt.customer_logo", { name: customerInfo.name })}
                      className="storeLogo"
                      src={customerInfo.logo}
                    />
                    <div className="details">
                      <div className="item-name">
                        {t("rate_store_experience", {
                          storeName: customerInfo.name,
                        })}
                      </div>
                      <div className="rating">
                        <StarsRating
                          value={storeReview.rating}
                          onClickDisabled={(value) => {
                            setSelectedRating(value);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {customerInfo.settings.embedNpsSurvey &&
                  npsSurvey &&
                  !external && (
                    <div
                      className={`order-item nps ${
                        npsSurvey.active ? "" : "done"
                      }`}
                      onClick={() => {
                        if (npsSurvey.active) {
                          setIsNpsModalVisible(true);
                          window.scrollTo(0, 0);
                        }
                      }}
                    >
                      {!npsSurvey.active && <div className="badge"></div>}
                      <div className="details">
                        <div className="item-name">
                          {npsSurvey.survey.npsQuestion}
                        </div>
                        <div className="rating">
                          <NpsScore
                            value={-1}
                            onClickDisabled={(value) => {
                              setNpsRating(value);
                              setIsNpsModalVisible(true);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                {/* Sellers section */}
                {customerInfo.settings.enableSellerReview && orderItems.sellers && orderItems.sellers.length > 0 && (
                  <>
                    {orderItems.sellers.map((seller) => {
                      return (
                        <div
                          key={seller.sellerId}
                          className={`order-item ${seller.active ? "" : "done"}`}
                          onClick={() => {
                            if (seller.active) {
                              setSellerReview({ ...seller });
                              setIsModalVisible(false);
                            }
                          }}
                        >
                          {!seller.active && <div className="badge"></div>}
                          {seller.logoUrl ? (
                            <div className="picture" aria-label={t("alt.seller_logo", { name: seller.displayName || seller.sellerName })}>
                              <img src={seller.logoUrl} alt={seller.displayName || seller.sellerName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                          ) : (
                            <div className="picture monogram" aria-label={t("alt.seller_logo", { name: seller.displayName || seller.sellerName })}>
                              {String(seller.displayName || seller.sellerName || "").trim().split(" ").slice(0,2).map(s => s[0]).join("")}
                            </div>
                          )}
                          <div className="details">
                            <div className="item-name">{t("rate_seller_experience", {
                          storeName: seller.displayName || seller.sellerName,
                        })}</div>
                            <div className="rating">
                              <StarsRating
                                value={seller.review ? seller.review.rating : 0}
                                onClickDisabled={(value) => {
                                  setSelectedRating(value);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          )}

          <RateProduct
            modal
            show={isModalVisible}
            customerInfo={customerInfo}
            product={selectedProduct}
            external={external}
            initialRating={selectedRating}
            onHide={async () => {
              // Recarrega pedido para atualizar status dos cards
              await getOrderDetails();
              setIsModalVisible(false);
            }}
            onComplete={(couponData) => {
              setAllowCoupon(true);
              // Se o review retornou um cupom com código, atualiza
              if (couponData) {
                setCoupon(couponData);
              }
            }}
            getNextItem={() => getNextActiveItem(selectedProduct)}
            onNavigateNext={handleNavigateToNext}
          />

          <RateSeller
            modal
            show={!!sellerReview}
            customer={customer}
            customerInfo={customerInfo}
            seller={sellerReview}
            orderId={orderId}
            onHide={() => setSellerReview(null)}
            onComplete={async (couponData) => {
              setSellerReview(null);
              setAllowCoupon(true);
              // Recarrega pedido para atualizar status, depois sobrescreve com cupom da review (se houver)
              await getOrderDetails();
              if (couponData) {
                setCoupon(couponData);
              }
            }}
          />

          <NetPromoterScoreSurvey
            show={isNpsModalVisible}
            modal={true}
            surveyData={npsSurvey}
            customerData={customerInfo}
            initialRating={npsRating}
            onHide={() => {
              getOrderDetails();
              setNpsRating(-1);
              setIsNpsModalVisible(false);
            }}
          />

          {allowCoupon && coupon && (
            <div className="coupon-div">
              
              <div className="coupon-text">
                <button className="close-coupon" onClick={() => {
                  setCoupon(null);
                  setAllowCoupon(false);
                }}>
                <img alt={t('alt.close')} src={closeButton} />
              </button>
                <p className="line1">
                  {t("coupon_received", {
                    discount: coupon.isPercentage
                      ? `${coupon.discountPercentage}%`
                      : coupon.discountPercentage,
                  })}
                </p>
                <p className="line2">
                  {t("coupon_use_code", { code: coupon.couponCode })}
                </p>
                <p className="line3">
                  {t("coupon_validity", { days: coupon.couponValidity })}
                </p>
                <p className="line4">
                  {!copyCoupon && (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        className="icone"
                      >
                        {" "}
                        <polygon
                          fill="var(--ci-primary-color, currentColor)"
                          points="408 432 376 432 376 464 112 464 112 136 144 136 144 104 80 104 80 496 408 496 408 432"
                          class="ci-primary"
                        />{" "}
                        <path
                          fill="var(--ci-primary-color, currentColor)"
                          d="M176,16V400H496V153.373L358.627,16ZM464,368H208V48H312V200H464Zm0-200H344V48h1.372L464,166.627Z"
                          class="ci-primary"
                        />{" "}
                      </svg>
                      <button
                        className="link-button"
                        onClick={(event) => handleCopy(event, coupon.couponCode)}
                      >
                        {t("copy_code")}
                      </button>
                    </>
                  )}
                  {copyCoupon && t("code_copied")}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
